import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, post } from "../../Services/ApiRequest";
import type {
  XFactorBriefingDetail,
  XFactorBriefingSummaryRow,
  XFactorOrg,
  XFactorPipelineStatus,
} from "../../Types/XFactorBriefing";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/**
 * Backend responses are wrapped by `utils.responses.response` as
 * `{ content, response: { status, message } }`. Unwrap once here so components
 * never deal with the envelope.
 */
const unwrap = (res: any) => res?.content ?? res;

export type XFactorKpiMetricDetail = {
  metricId?: string;
  name: string;
  value: number;
  weight: number;
  nominalWeight?: number;
  actualValue?: string;
  uom?: string;
  trendDirection?: string;
  trendMagnitudePct?: number;
  hasData: boolean;
};

export type XFactorKpiMetricsBreakdown = Record<
  string,
  Record<string, XFactorKpiMetricDetail[]>
>;

export type FetchXFactorKpiMetricsBreakdownPayload = {
  accountIds: string[];
  measurementDate: string;
  datasetKind: string;
};

export type FetchXFactorKpiAccountBriefingsPayload = {
  accountIds: string[];
  view: "category" | "full" | "table";
  datasetKind: string;
  categoryId?: string;
  measurementDate?: string | null;
  granularity?: string | null;
  period?: string | null;
};

export type XFactorKpiAccountBriefingResult = {
  account_id?: string;
  briefing?: unknown;
  [key: string]: unknown;
};

export type XFactorKpiAccountBriefingsResponse = {
  results?: XFactorKpiAccountBriefingResult[];
  columns?: unknown;
  [key: string]: unknown;
};

interface XFactorBriefingState {
  orgs: { items: XFactorOrg[]; loading: boolean; error: string | null };
  list: {
    items: XFactorBriefingSummaryRow[];
    customerId: string | null;
    loading: boolean;
    error: string | null;
  };
  detail: {
    data: XFactorBriefingDetail | null;
    digestId: string | null;
    loading: boolean;
    error: string | null;
  };
  kpiMetricsBreakdown: {
    data: XFactorKpiMetricsBreakdown;
    loading: boolean;
    error: string | null;
  };
  /** Bulk approve pipeline status, keyed by EB account id. */
  pipelineStatus: Record<string, XFactorPipelineStatus | null>;
}

const initialState: XFactorBriefingState = {
  orgs: { items: [], loading: false, error: null },
  list: { items: [], customerId: null, loading: false, error: null },
  detail: { data: null, digestId: null, loading: false, error: null },
  kpiMetricsBreakdown: { data: {}, loading: false, error: null },
  pipelineStatus: {},
};

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const parseKpiMetricsBreakdown = (
  rawBreakdown: unknown
): XFactorKpiMetricsBreakdown => {
  const parsed: XFactorKpiMetricsBreakdown = {};
  if (!rawBreakdown || typeof rawBreakdown !== "object") return parsed;

  for (const [accountId, categories] of Object.entries(
    rawBreakdown as Record<string, unknown>
  )) {
    const byCategory: Record<string, XFactorKpiMetricDetail[]> = {};
    for (const [categoryId, metrics] of Object.entries(
      (categories as Record<string, unknown>) ?? {}
    )) {
      byCategory[categoryId] = (Array.isArray(metrics) ? metrics : []).map(
        (m: any) => ({
          metricId: m.metric_id ?? undefined,
          name: String(m.name ?? m.metric_id ?? ""),
          value: isFiniteNumber(m?.value) ? m.value : 0,
          weight: isFiniteNumber(m?.weight) ? m.weight : 0,
          nominalWeight: isFiniteNumber(m?.nominal_weight)
            ? m.nominal_weight
            : undefined,
          actualValue:
            m.actual_value !== undefined && m.actual_value !== null
              ? String(m.actual_value)
              : undefined,
          uom: m.uom ?? undefined,
          trendDirection: m.trend_direction ?? undefined,
          trendMagnitudePct: isFiniteNumber(m?.trend_magnitude_pct)
            ? m.trend_magnitude_pct
            : undefined,
          hasData: Boolean(m?.has_data),
        })
      );
    }
    parsed[accountId] = byCategory;
  }
  return parsed;
};

const message = (error: any, fallback: string) =>
  error?.response?.data?.detail ||
  error?.response?.data?.response?.message ||
  error?.message ||
  fallback;

export const fetchXFactorOrgs = createAsyncThunk(
  "xfactorBriefing/orgs",
  async (_, { rejectWithValue }) => {
    try {
      return unwrap(await get(API_URLS.xfactorBriefingsOrgs()));
    } catch (error: any) {
      return rejectWithValue(message(error, "Failed to load organizations"));
    }
  }
);

export const fetchXFactorBriefingsForAccount = createAsyncThunk(
  "xfactorBriefing/listForAccount",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorAccountBriefings(), {
          account_ids: [customerId],
        })
      );
      const account =
        (res?.accounts || []).find(
          (row: { customer_id?: string }) => row.customer_id === customerId
        ) || res?.accounts?.[0];
      return {
        customerId,
        customer_id: account?.customer_id ?? customerId,
        total: account?.total ?? 0,
        briefings: account?.briefings ?? [],
      };
    } catch (error: any) {
      return rejectWithValue(message(error, "Failed to load briefings"));
    }
  }
);

export const fetchXFactorBriefingDetail = createAsyncThunk(
  "xfactorBriefing/detail",
  async (digestId: string, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorBriefingDigests(), {
          digest_ids: [digestId],
        })
      );
      const briefing =
        (res?.briefings || []).find(
          (row: { digest_id?: string }) => row.digest_id === digestId
        ) || res?.briefings?.[0];
      if (!briefing) {
        return rejectWithValue("Failed to load briefing");
      }
      return briefing;
    } catch (error: any) {
      return rejectWithValue(message(error, "Failed to load briefing"));
    }
  }
);

export const fetchXFactorKpiMetricsBreakdown = createAsyncThunk<
  XFactorKpiMetricsBreakdown,
  FetchXFactorKpiMetricsBreakdownPayload,
  { rejectValue: string }
>(
  "xfactorBriefing/kpiMetricsBreakdown",
  async ({ accountIds, measurementDate, datasetKind }, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorKpiMetricsBreakdown(), {
          account_ids: accountIds,
          measurement_date: measurementDate,
          dataset_kind: datasetKind,
        })
      );
      return parseKpiMetricsBreakdown(res?.breakdown ?? {});
    } catch (error: any) {
      return rejectWithValue(
        message(error, "Failed to load KPI metrics breakdown")
      );
    }
  }
);

/**
 * Shared by every caller of POST /kpi/account-briefings. Each caller asks for
 * a different view and account set, so the result is returned to the caller
 * instead of being stored on the slice.
 */
export const fetchXFactorKpiAccountBriefings = createAsyncThunk<
  XFactorKpiAccountBriefingsResponse,
  FetchXFactorKpiAccountBriefingsPayload,
  { rejectValue: string }
>(
  "xfactorBriefing/kpiAccountBriefings",
  async (
    {
      accountIds,
      view,
      datasetKind,
      categoryId,
      measurementDate,
      granularity,
      period,
    },
    { rejectWithValue }
  ) => {
    try {
      return unwrap(
        await post(API_URLS.xfactorKpiAccountBriefings(), {
          account_ids: accountIds,
          view,
          dataset_kind: datasetKind,
          ...(categoryId ? { category_id: categoryId } : {}),
          ...(measurementDate ? { measurement_date: measurementDate } : {}),
          ...(granularity ? { granularity } : {}),
          ...(granularity && period ? { period } : {}),
        })
      );
    } catch (error: any) {
      return rejectWithValue(
        message(error, "Failed to load KPI account briefings")
      );
    }
  }
);

export const fetchXFactorAccountPipelineStatus = createAsyncThunk<
  Record<string, XFactorPipelineStatus | null>,
  string[],
  { rejectValue: string }
>(
  "xfactorBriefing/accountPipelineStatus",
  async (accountIds, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorAccountPipelineStatus(), {
          account_ids: accountIds,
        })
      );
      return Object.fromEntries(
        accountIds.map(id => [id, res?.accounts?.[id] ?? null])
      );
    } catch (error: any) {
      return rejectWithValue(message(error, "Failed to load pipeline status"));
    }
  }
);

const XFactorBriefingSlice = createSlice({
  name: "xfactorBriefing",
  initialState,
  reducers: {
    clearXFactorDetail: state => {
      state.detail = initialState.detail;
    },
  },
  extraReducers: builder => {
    builder
      // orgs
      .addCase(fetchXFactorOrgs.pending, state => {
        state.orgs.loading = true;
        state.orgs.error = null;
      })
      .addCase(fetchXFactorOrgs.fulfilled, (state, action) => {
        state.orgs.loading = false;
        state.orgs.items = action.payload?.orgs ?? [];
      })
      .addCase(fetchXFactorOrgs.rejected, (state, action) => {
        state.orgs.loading = false;
        state.orgs.error = action.payload as string;
      })

      // list
      .addCase(fetchXFactorBriefingsForAccount.pending, state => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchXFactorBriefingsForAccount.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.items = action.payload?.briefings ?? [];
        state.list.customerId = action.payload?.customerId ?? null;
      })
      .addCase(fetchXFactorBriefingsForAccount.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })

      // detail
      .addCase(fetchXFactorBriefingDetail.pending, state => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchXFactorBriefingDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload;
        state.detail.digestId = action.payload?.digest_id ?? null;
      })
      .addCase(fetchXFactorBriefingDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload as string;
      })

      // kpi metrics breakdown
      .addCase(fetchXFactorKpiMetricsBreakdown.pending, state => {
        state.kpiMetricsBreakdown.loading = true;
        state.kpiMetricsBreakdown.error = null;
      })
      .addCase(fetchXFactorKpiMetricsBreakdown.fulfilled, (state, action) => {
        state.kpiMetricsBreakdown.loading = false;
        state.kpiMetricsBreakdown.data = action.payload;
      })
      .addCase(fetchXFactorKpiMetricsBreakdown.rejected, (state, action) => {
        state.kpiMetricsBreakdown.loading = false;
        state.kpiMetricsBreakdown.data = {};
        state.kpiMetricsBreakdown.error = action.payload as string;
      })

      // account pipeline status
      .addCase(fetchXFactorAccountPipelineStatus.fulfilled, (state, action) => {
        Object.assign(state.pipelineStatus, action.payload);
      })
      .addCase(fetchXFactorAccountPipelineStatus.rejected, (state, action) => {
        for (const id of action.meta.arg) {
          state.pipelineStatus[id] = null;
        }
      });
  },
});

export const { clearXFactorDetail } = XFactorBriefingSlice.actions;
export default XFactorBriefingSlice.reducer;
